import orderBy from "lodash-es/orderBy"
import React from "react"
import { useFragment } from "react-relay"
import { graphql } from "relay-runtime"

import { PersonSidePanelNotes_person$key } from "./__generated__/PersonSidePanelNotes_person.graphql"
import { PersonSidePanelNotes_user$key } from "./__generated__/PersonSidePanelNotes_user.graphql"

import { track } from "~/helpers/analytics"

import {
  personNoteCreateRelay,
  personNoteDeleteRelay,
  personNoteUpdateRelay,
} from "~/mutations/PersonNote"

import NotesContainer from "~/Notes/components/NotesContainer"
import { usePermissions } from "~/Permissions/usePermissions"

type Props = {
  person: PersonSidePanelNotes_person$key
  user: PersonSidePanelNotes_user$key
}

const PersonSidePanelNotes = (props: Props) => {
  const user = useFragment(
    graphql`
      fragment PersonSidePanelNotes_user on users {
        id
      }
    `,
    props.user,
  )

  const person = useFragment(
    graphql`
      fragment PersonSidePanelNotes_person on people {
        id
        email
        is_placeholder
        people_notes {
          id
          note
          created_at
          updated_at
          creator_email
          created_by
          user_id
        }
      }
    `,
    props.person,
  )

  const sortedNotes = orderBy(
    person.people_notes,
    (note) => note.created_at,
    "asc",
  )

  const { can, subject } = usePermissions()

  const canCreateNote = () =>
    can(
      "create",
      subject("PersonNote", {
        user,
        person: person,
      }),
    )

  const canDeleteNote = ({ id }: { id: number }) =>
    can(
      "delete",
      subject("PersonNote", {
        user: { id },
        person: person,
      }),
    )

  const canEditNote = ({ id }: { id: number }) =>
    can(
      "edit",
      subject("PersonNote", {
        user: { id },
        person: person,
      }),
    )

  const onDeleteNote = (id: number) => {
    if (!person.is_placeholder) {
      track("Person Note Deleted")
    }

    void personNoteDeleteRelay({ id })
  }

  const onUpdateNote = async (id: number, note: string) => {
    await personNoteUpdateRelay({
      noteId: id,
      note,
    })
  }

  const onCreateNote = async (newNote: string) => {
    if (person.is_placeholder) {
      track("Comment Created", { type: "Placeholder" })
    } else {
      track("Note Added", { type: "Person" })
    }

    await personNoteCreateRelay({
      note: newNote,
      personId: person.id,
    })
  }

  return (
    <NotesContainer
      notes={sortedNotes}
      permissions={{
        canCreate: canCreateNote,
        canDelete: canDeleteNote,
        canEdit: canEditNote,
      }}
      onUpdate={onUpdateNote}
      onDelete={onDeleteNote}
      onCreate={onCreateNote}
    />
  )
}

export default PersonSidePanelNotes
